'use client';

/**
 * Canvas Panel
 *
 * Main content area showing widgets based on mode and selection.
 * Strategy pattern: CANVAS_WIDGETS maps CanvasStrategy → component.
 * Uses Total Matrix for canvas_override when selection changes.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain content
 */

import { ComponentType } from 'react';
import { X, Pin, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useTabStore } from '@/lib/stores/tab-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import {
  getTabIcon,
  getViewTabTypes,
  getViewPlaceholder,
  registerTabWidget,
} from '@/lib/registry';
import { testIds } from '@/lib/test-utils/selectors';

// Import widgets
import { VendorCollectionsWidget } from '@/components/widgets/vendor-collections-widget';
import { VendorListWidget } from '@/components/widgets/vendor-list-widget';
import { ProjectGraphCompare } from '@/components/widgets/project-graph-compare';
import { RFQComparisonWidget } from '@/components/widgets/rfq-comparison-widget';
import { CartWidget } from '@/components/widgets/cart-widget';
import { ObeyaWidget } from '@/components/widgets/obeya-widget';
import { IntentWidget } from '@/components/widgets/intent-widget';
import { IntentModeWidget } from '@/components/widgets/intent-mode-widget';
import { ArchDocsWidget } from '@/components/widgets/arch-docs-widget';
import { AgentFlowWidget } from '@/components/widgets/agent-flow-widget';
import { TestDashboardWidget } from '@/components/widgets/test-dashboard-widget';
import { TestResultsWidget } from '@/components/widgets/test-results-widget';
import { SystemObeyaWidget } from '@/components/widgets/system-obeya-widget';
import { VendorQuoteFormWidget } from '@/components/widgets/vendor-quote-form-widget';
import { TestCoverageGraph } from '@/components/widgets/test-coverage-graph';
import { RFIWidget } from '@/components/widgets/rfi-widget';
import { AddVendorFormWidget } from '@/components/widgets/add-vendor-form-widget';
import { AddProductFormWidget } from '@/components/widgets/add-product-form-widget';
import { AddScheduleFormWidget } from '@/components/widgets/add-schedule-form-widget';
import { DesignIntentGraphWidget } from '@/components/widgets/design-intent-graph-widget';
import { CardsCanvasWidget } from '@/components/canvas/cards/cards-canvas-widget';
import { MousePointerClick } from 'lucide-react';

/** Blank canvas — shown when no entity is selected in a mode. */
function EmptyCanvasWidget() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
      <MousePointerClick className="w-12 h-12 mb-4 opacity-40" />
      <p className="text-sm">Select an item from the explorer</p>
    </div>
  );
}

// Legacy registry support (for backward compatibility during migration)
registerTabWidget('vendor', VendorCollectionsWidget);
registerTabWidget('project', ProjectGraphCompare);
registerTabWidget('rfq', RFQComparisonWidget);
registerTabWidget('cart', CartWidget);
registerTabWidget('obeya', ObeyaWidget);
registerTabWidget('intent', IntentModeWidget);

// ============================================================
// CANVAS_WIDGETS - Local strategy → component map
// ============================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyWidget = ComponentType<any>;

/**
 * Maps CanvasStrategy values to React components.
 * Add new widgets here - no registration needed.
 *
 * Note: Using `any` for props because widgets have varying signatures.
 * Type safety is maintained by the tab store which tracks entityId.
 */
const CANVAS_WIDGETS: Record<string, AnyWidget> = {
  project_graph: ProjectGraphCompare,
  vendor_catalog: VendorCollectionsWidget,
  rfq_comparison: RFQComparisonWidget,
  order_view: ProjectGraphCompare, // Placeholder - reuse graph for now
  obeya: ObeyaWidget,
  obeya_conversations: ObeyaWidget, // Same widget - handles conversations via canvasStrategy check
  intent: IntentModeWidget,                // Intent mode — query (IntentWidget) or grid (IntentGridWidget) based on store
  arch_canvas: ArchDocsWidget, // Hybrid tree/table/graph for architecture
  docs: ArchDocsWidget, // Same widget for docs mode
  agent_flow: AgentFlowWidget, // Agent simulation step visualization
  test_dashboard: TestDashboardWidget, // Test statistics dashboard (copilot)
  test_results: TestResultsWidget, // Test results with products (canvas)
  system_obeya: SystemObeyaWidget, // System Obeya analytical reports
  vendor_quote_form: VendorQuoteFormWidget, // Vendor RFQ quote submission form
  test_coverage_graph: TestCoverageGraph,  // Architecture ↔ Testing coverage spiderweb
  rfi: RFIWidget,                          // RFI conversation threads / board / matrix
  empty: EmptyCanvasWidget,               // Blank canvas — "select from the explorer"
  add_vendor: AddVendorFormWidget,          // Vendor registration form
  add_product: AddProductFormWidget,        // Product creation form (discovery)
  add_schedule: AddScheduleFormWidget,      // Schedule PDF ingest form (intent)
  design_intent_graph: DesignIntentGraphWidget,  // Actor-centric 2-hop graph (discovery, style guide selected)
  // Cards-everywhere canvas — generic widget that reads `canvas_cards`
  // placements from the matrix and paints via the LayoutEngine. Used
  // by `mode1` (Workspace) for the cards-everywhere prototype. See
  // klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md.
  cards: CardsCanvasWidget,
};

export function Canvas() {
  const { tabs, activeTabId, setActiveTab, closeTab, togglePin } = useTabStore();
  const activeExplorerView = useLayoutStore((state) => state.activeExplorerView);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const graphProjectId = useGraphSelectionStore((s) => s.projectId);
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // Canonical canvas selection chain — `explorerSelection ?? activeTab.type`.
  // Folds in sub-mode selections (e.g. add_vendor, add_product) that raw
  // tab.type misses. The OVERLAY consumes the same hook for matrix lookup;
  // the canvas now does too, so explorer-driven selection swaps re-route
  // both surfaces in lockstep.
  const { strategySelection } = useViewportSelection();

  // Filter tabs to only show ones relevant to current explorer view
  // Cart tabs are always visible (floating drawer can expand to canvas)
  const relevantTabTypes = getViewTabTypes(activeExplorerView);
  const visibleTabs = tabs.filter((t) => relevantTabTypes.includes(t.type) || t.type === 'cart');

  // Find active tab among visible tabs
  // Only show a tab if explicitly selected (activeTabId set), don't auto-select first tab
  const activeTab = activeTabId
    ? visibleTabs.find((t) => t.id === activeTabId) ?? null
    : null;

  // Get canvas config from Total Matrix
  // Two-tier lookup: base config from strategySelection (explorerSelection ??
  // activeTab.type), then canvas_override from graph selection.
  const graphSelectionType = selectedNode?.type ?? null;
  const baseSelectionType = strategySelection;

  // Base config: what widget to show for this tab type (e.g., project → project_graph)
  const baseConfig = getViewportConfig(activeExplorerView, baseSelectionType);
  const baseStrategy = baseConfig?.canvas?.strategy ?? null;

  // Override: graph node selection can swap the canvas widget (e.g., clicking RFQ node → rfq_comparison)
  const overrideConfig = graphSelectionType
    ? getViewportConfig(activeExplorerView, graphSelectionType)
    : null;
  const overrideStrategy = overrideConfig?.canvas?.strategy || null;

  // Use override only if it resolves to a known widget different from base
  const isOverrideActive = overrideStrategy && overrideStrategy !== baseStrategy && CANVAS_WIDGETS[overrideStrategy];
  const canvasStrategy = isOverrideActive ? overrideStrategy : baseStrategy;

  // When override is active, the widget should use the selected node's ID, not the tab's.
  // Fallback to graph store projectId for entities (like MCP) that share the parent project's graph.
  const widgetEntityId = isOverrideActive ? selectedNode?.id : (activeTab?.entityId ?? graphProjectId);
  const widgetTitle = isOverrideActive ? selectedNode?.label : (activeTab?.title ?? selectedNode?.label);

  // Always prefer the more specific (graph-selection) config props when available,
  // even if the strategy is the same. This lets system_obeya pass different default_view
  // per report without requiring a strategy change.
  const canvasProps = (overrideConfig ?? baseConfig)?.canvas?.props ?? {};

  const renderPlaceholder = () => {
    const placeholder = getViewPlaceholder(activeExplorerView);
    const Icon = placeholder.icon;

    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Icon className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg">{placeholder.title}</p>
        <p className="text-sm">{placeholder.subtitle}</p>
      </div>
    );
  };

  const renderTabContent = () => {
    const Widget = CANVAS_WIDGETS[canvasStrategy];
    if (Widget) {
      return (
        <Widget
          key={`${canvasStrategy}-${widgetEntityId ?? 'none'}`}
          entityId={widgetEntityId}
          title={widgetTitle}
          {...canvasProps}
        />
      );
    }

    // Unknown or missing strategy — surface it visibly so matrix misconfigs don't silently fail
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive/80">
        <AlertTriangle className="w-12 h-12 mb-3" />
        <p className="text-sm font-medium">{canvasStrategy ? 'Unknown canvas strategy' : 'No canvas strategy from matrix'}</p>
        <code className="mt-1 px-2 py-1 rounded bg-muted text-xs">
          {canvasStrategy ?? '(null)'}
        </code>
        <p className="text-xs text-muted-foreground mt-2">
          Mode: {activeExplorerView} • Tab: {baseSelectionType ?? 'none'} • Graph: {graphSelectionType ?? 'none'}
        </p>
      </div>
    );
  };

  return (
    <div data-testid={testIds.canvas.container} className="h-full flex flex-col bg-background">
      {/* Tab Bar - only show if there are visible tabs */}
      {visibleTabs.length > 0 && (
        <div data-testid={testIds.canvas.tabBar} className="flex items-center border-b border-panel-border bg-panel overflow-x-auto">
          {visibleTabs.map((tab) => {
            const Icon = getTabIcon(tab.type);
            return (
              <div
                key={tab.id}
                data-testid={testIds.canvas.tab(tab.id)}
                className={cn(
                  'group flex items-center gap-1.5 px-3 py-2 text-sm border-r border-panel-border cursor-pointer shrink-0',
                  'hover:bg-accent/50 transition-colors',
                  activeTab?.id === tab.id
                    ? 'bg-background text-foreground'
                    : 'text-muted-foreground'
                )}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate max-w-32">{tab.title}</span>
                <div className="flex items-center gap-0.5 ml-1">
                  {tab.pinned ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePin(tab.id);
                      }}
                      className="p-0.5 hover:bg-muted rounded"
                    >
                      <Pin className="w-3 h-3 text-primary" />
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePin(tab.id);
                        }}
                        className="p-0.5 hover:bg-muted rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Pin className="w-3 h-3" />
                      </button>
                      <button
                        data-testid={testIds.canvas.tabClose(tab.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          closeTab(tab.id);
                        }}
                        className="p-0.5 hover:bg-muted rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tab Content */}
      <div data-testid={testIds.canvas.content} className="flex-1 overflow-hidden">
        {renderTabContent()}
      </div>
    </div>
  );
}
